import React, { useState } from 'react';
import axiosInstance from '../../utils/axios';
import { useNavigate } from "react-router-dom";
import { format } from 'date-fns';
import { fnOnChange, showToast } from '../reusables/CommonFunction';
import { Badge, Table, Modal, Form, Button } from "react-bootstrap";
import MESSAGES from '../../utils/Constants';
import ModalDelete from '../reusables/ModalDelete';
import { ToastContainer } from 'react-toastify';
import CustomPagination from '../reusables/CustomPagination';
import BusyLoaderOverlay from '../reusables/BusyLoaderOverlay';
import { formatDisplayTime } from '../../utils/formatter';
import CustomTimePicker from '../reusables/CustomTimePicker';


export function AppointmentTable({ appointments, selectedDate, userDetails, onPressSearchAppointments }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [appointmentDetails, setAppointmentDetails] = useState({});
    const [appointmentDetailsErrors, setAppointmentDetailsErrors] = useState({});
    const [isSubmitting, setSubmitting] = useState(false);
    const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState({});
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const salutation = { '1': 'DR.', '2': 'MR.', '3': 'MRS.', '4': 'MISS', '5': 'MASTER' }
    const filteredAppointments = appointments.filter(
        apt => format(new Date(apt.appointment_date), 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd')
    );

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };
    const pageSize = 5;
    const startIndex = (currentPage - 1) * pageSize;
    const paginatedAppointments = filteredAppointments.slice(startIndex, startIndex + pageSize);

    /* Open New Patient Screen with Existing / New Patient Data */
    const onPressConsult = (oEvent, pAppointment) => {
        oEvent.preventDefault();
        pAppointment.status = "C";
        if (pAppointment.visitor_type === 'E') {
            navigate(`/patient/${pAppointment.patient_reference_id}`, { state: { ...pAppointment } })
        } else {
            navigate(`/patient/new`, { state: { ...pAppointment } })
        }
    }

    /* Open Reschedule Screen with Existing / New Patient Data */
    const onPressReschedule = (oEvent, pVisitorId) => {
        oEvent.preventDefault();
        setIsRescheduleModalOpen({ ['isModalOpen_' + pVisitorId]: true })
    }

    /* Cancel Appointment */
    const onPressCancel = (oEvent, pVisitorId) => {
        oEvent.preventDefault();
        setIsDeleteModalOpen({ ['isModalOpen_' + pVisitorId]: true })
    }

    const onPressSubmitDeleteAppointment = (oEvent, pVisitorId) => {
        oEvent.preventDefault();
        setLoading(true);
        axiosInstance
            .delete(`/appointments/${pVisitorId}/`)
            .then(response => {
                if (response.status === 200) {
                    showToast("success", response.data.message, { onClose: () => onPressSearchAppointments() });
                    setIsDeleteModalOpen({ ['isModalOpen_' + pVisitorId]: false })
                } else if (response.status === 403 && response.data.detail === "Your clinic plan has expired.") {
                    showToast("error", "You cannot delete this appointment as your clinic plan has expired. Please renew your plan to manage appointments.");
                }
            })
            .catch(error => {
                if (error.response?.status === 403 && error.response?.data?.detail === "Your clinic plan has expired.") {
                    showToast("error", "You cannot delete this appointment as your clinic plan has expired. Please renew your plan to manage appointments.");
                } else {
                    showToast("error", "Failed to delete appointment. Please try again later");
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }

    /* Reschedule Appointment */
    const onPressSubmitRescheduleAppointment = (oEvent, pAppointment) => {
        oEvent.preventDefault();

        // Validate appointment details
        const errors = {};
        if (!appointmentDetails.appointment_date) {
            errors.appointment_date = "Appointment date is required";
        }
        if (!appointmentDetails.appointment_time) {
            errors.appointment_time = "Appointment time is required";
        }
        if (Object.keys(errors).length > 0) {
            setAppointmentDetailsErrors(errors);
            return;
        }

        setSubmitting(true);
        setLoading(true);
        axiosInstance.put(`/appointments/${pAppointment.visitor_id}/`, {
            clinic_id: pAppointment.clinic_id,
            mobile_number: pAppointment.mobile_number,
            visitor_name: pAppointment.visitor_name,
            appointment_date: appointmentDetails.appointment_date,
            appointment_time: appointmentDetails.appointment_time,
            amount: '',
            status: "U"
        })
            .then(response => {
                if (response.status === 200) {
                    showToast("success", response.data.message, { onClose: () => onPressSearchAppointments() });
                    setIsRescheduleModalOpen({ ['isModalOpen_' + pAppointment.visitor_id]: false })
                } else if (response.status === 403 && response.data.detail === "Your clinic plan has expired.") {
                    showToast("error", "You cannot reschedule this appointment as your clinic plan has expired. Please renew your plan to manage appointments.");
                }
            })
            .catch(error => {
                if (error.response?.status === 403 && error.response?.data?.detail === "Your clinic plan has expired.") {
                    showToast("error", "You cannot reschedule this appointment as your clinic plan has expired. Please renew your plan to manage appointments.");
                } else {
                    showToast("error", "Failed to delete appointment. Please try again later");
                }
            })
            .finally(() => {
                setLoading(false);
                setSubmitting(false);
            });
    }

    const getStatusVariant = (status) => {
        switch (status) {
            case MESSAGES.APPOINTMENT_UPCOMING: return 'warning';
            case MESSAGES.APPOINTMENT_COMPLETED: return 'success';
            case MESSAGES.APPOINTMENT_MISSED: return 'danger';
            default: return 'secondary';
        }
    };

    return (
        <div>
            <h3 className="mb-3">
                Appointments for {format(selectedDate, 'MMMM d, yyyy')}
            </h3>
            <Table bordered hover responsive className="align-middle">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Visitor Name</th>
                        <th>Mobile No</th>
                        <th>Visitor Type</th>
                        <th>Clinic Branch</th>
                        <th>Time</th>
                        <th>Status</th>
                        <th>Amount</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {paginatedAppointments.map((appointment, index) => (
                        <tr key={appointment.visitor_id}>
                            <td>{index + 1}</td>
                            <td className="text-truncate text-left" style={{ maxWidth: "200px" }}>
                                <div className="text-truncate w-100" title={salutation[appointment.salutation] + " " + appointment.visitor_name}>
                                    {salutation[appointment.salutation]} {appointment.visitor_name}
                                </div>
                            </td>
                            <td>{appointment.mobile_number}</td>
                            <td>{appointment.visitor_type === 'N' ? 'New' : 'Existing'}</td>
                            <td>{appointment.branch_name}</td>
                            <td>{formatDisplayTime(appointment.appointment_time.substr(0, 5))}</td>
                            <td>
                                <Badge bg={getStatusVariant(appointment.status_display)}>
                                    {appointment.status_display}
                                </Badge>
                            </td>
                            <td>{appointment.amount ? `₹ ${appointment.amount}` : 'N/A'}</td>
                            <td>
                                {userDetails.user_type !== "M" ? (
                                    <>
                                        {userDetails.is_clinic_plan_expired === false ? (
                                            <>
                                                {appointment.status_display === MESSAGES.APPOINTMENT_UPCOMING ?
                                                    <>
                                                        {userDetails.user_type === "D" ? (
                                                            <Badge type='button' bg='success' size='sm' aria-label="Consult" onClick={oEvent => onPressConsult(oEvent, appointment)} className="me-2 d-none d-md-inline-block">Consult</Badge>
                                                        ) : null}
                                                        <Badge type='button' bg='primary' size='sm' aria-label="Reschedule" onClick={oEvent => onPressReschedule(oEvent, appointment.visitor_id)} className="me-2">Reschedule</Badge>
                                                        <Badge type='button' bg='danger' size='sm' aria-label="Cancel" onClick={oEvent => onPressCancel(oEvent, appointment.visitor_id)}>Cancel</Badge>

                                                        <ModalDelete show={isDeleteModalOpen['isModalOpen_' + appointment.visitor_id]} modalTitle="Appointment" modalBody={`${salutation[appointment.salutation]} ${appointment.visitor_name}`}
                                                            onHide={() => setIsDeleteModalOpen({ ['isModalOpen_' + appointment.visitor_id]: false })}
                                                            onSubmit={oEvent => onPressSubmitDeleteAppointment(oEvent, appointment.visitor_id)} />

                                                        <Modal size="lg" backdrop="static" keyboard={false} centered
                                                            show={isRescheduleModalOpen['isModalOpen_' + appointment.visitor_id]} onHide={() => setIsRescheduleModalOpen({ ['isModalOpen_' + appointment.visitor_id]: false })} >
                                                            <Modal.Header closeButton>
                                                                <Modal.Title>Reschedule Appointment</Modal.Title>
                                                            </Modal.Header>
                                                            <Modal.Body>
                                                                <Form autoComplete="off" noValidate>
                                                                    <Form.Group className="mb-3" controlId="formAppointmentDate">
                                                                        <Form.Label className="required">Appointment Date</Form.Label>
                                                                        <Form.Control
                                                                            type="date"
                                                                            name="appointment_date"
                                                                            value={appointmentDetails.appointment_date || ""}
                                                                            onChange={(oEvent) => fnOnChange(oEvent, appointmentDetails, setAppointmentDetails, appointmentDetailsErrors, setAppointmentDetailsErrors)}
                                                                            isInvalid={appointmentDetailsErrors.appointment_date}
                                                                        />
                                                                        <Form.Control.Feedback type="invalid">{appointmentDetailsErrors.appointment_date}</Form.Control.Feedback>
                                                                    </Form.Group>

                                                                    <Form.Group className="mb-3" controlId="formAppointmentTime">
                                                                        <Form.Label className="required">Appointment Time</Form.Label>
                                                                        <CustomTimePicker
                                                                            controlId="formUpdateAppointmentTimePicker"
                                                                            name="appointment_time"
                                                                            value={appointmentDetails.appointment_time || ""}
                                                                            onChange={(oEvent) => fnOnChange(oEvent, appointmentDetails, setAppointmentDetails, appointmentDetailsErrors, setAppointmentDetailsErrors)}
                                                                            isInvalid={appointmentDetailsErrors.appointment_time}
                                                                        />
                                                                        <Form.Control.Feedback type="invalid">{appointmentDetailsErrors.appointment_time}</Form.Control.Feedback>
                                                                    </Form.Group>
                                                                </Form>
                                                            </Modal.Body>
                                                            <Modal.Footer>
                                                                <Button type="submit" variant="primary" className="form-control" onClick={(oEvent) => onPressSubmitRescheduleAppointment(oEvent, appointment)}>
                                                                    {isSubmitting ? 'Appointment Rescheduling...' : 'Reschedule Appointment'}
                                                                </Button>
                                                            </Modal.Footer>
                                                        </Modal>
                                                    </>
                                                    : <></>}
                                            </>
                                        ) : null}
                                    </>) : null}
                            </td>
                        </tr>
                    ))}
                    {paginatedAppointments.length === 0 && (
                        <tr>
                            <td colSpan={7} className="text-center text-muted">
                                No appointments for this date
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
            {filteredAppointments.length > pageSize && (
                <CustomPagination
                    totalCount={filteredAppointments.length}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                />
            )}
            <ToastContainer />
            <BusyLoaderOverlay show={loading} />
        </div>
    );
}